import React, { useState, useEffect } from 'react';
import { Link } from 'react-router'; // Changed 'react-router' to 'react-router-dom'
import { useAuth } from '../../hooks/useAuth';

const VITE_API_URL = import.meta.env.VITE_API_URL;

function SessionCalendar() {
    const { token, doctor } = useAuth(); 
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [doctorNameDisplay, setDoctorNameDisplay] = useState('');
    const [viewHistory, setViewHistory] = useState(false);
    
    // Default Date to Today (Local Format YYYY-MM-DD)
    // new Date().toISOString() can sometimes be 'yesterday' if it's early morning locally.
    // Use this safer method for local date default:
    const today = new Date();
    const localDateStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

    const [newSession, setNewSession] = useState({
        date: localDateStr,
        session_name: 'Morning',
        start_time: '09:00',
        end_time: '13:00',
        max_patients: 25
    });

    const fetchSessions = async () => {
            setLoading(true);
            try {
                // Toggle URL query
                const mode = viewHistory ? 'history' : 'current';
                const limit = viewHistory ? 50 : 30;
    
                const res = await fetch(`${VITE_API_URL}/schedules/my-sessions?view=${mode}&limit=${limit}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setSessions(data);
                    // ... doctor name logic
                    // Set the doctor name from the first result if available
                if (data.length > 0) {
                    setDoctorNameDisplay(data[0].doctor_name);
                }
                }
            } catch (e) { } 
            finally { setLoading(false); }
        };
    
        // Re-fetch when toggle changes
        useEffect(() => { fetchSessions(); }, [token, viewHistory]);

    const handleCreate = async (e) => {
        e.preventDefault();
        
        // Overlap Check (Simplified: Check same date + shift name)
        const conflict = sessions.some(s => {
             // Extract date string part to compare reliably
             const sDate = typeof s.date === 'string' ? s.date.substring(0, 10) : new Date(s.date).toISOString().substring(0, 10);
             return sDate === newSession.date && s.session_name === newSession.session_name;
        });

        if (conflict) {
             alert(`A ${newSession.session_name} session already exists on this date.`);
             return;
        }

        try {
            const res = await fetch(`${VITE_API_URL}/schedules/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(newSession)
            });
            if (res.ok) {
                alert('Session Created Successfully');
                fetchSessions();
                
                // Auto-advance date
                const nextDate = new Date(newSession.date);
                nextDate.setDate(nextDate.getDate() + 1);
                const nextDateStr = nextDate.toISOString().split('T')[0];
                setNewSession({...newSession, date: nextDateStr});
            } else {
                 const err = await res.json();
                 alert(`Error: ${err.message}`);
            }
        } catch (e) { alert('Network Error'); }
    };

    const handleDelete = async (id) => {
        if(!window.confirm("Permanently remove this session?\n(Only allowed if no active appointments exist)")) return;
        
        try {
            const res = await fetch(`${VITE_API_URL}/schedules/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                setSessions(sessions.filter(s => s.schedule_id !== id));
            } else {
                const data = await res.json();
                alert(data.message);
            }
        } catch (e) { alert('Delete failed'); }
    };

    const cloneToForm = (session) => {
        const dateObj = new Date(session.date);
        dateObj.setDate(dateObj.getDate() + 7);
        const nextWeekStr = dateObj.toISOString().split('T')[0];
        
        setNewSession({
            date: nextWeekStr,
            session_name: session.session_name,
            start_time: session.start_time,
            end_time: session.end_time,
            max_patients: session.max_patients
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="max-w-7xl mx-auto p-6 bg-white rounded-xl shadow-lg border border-gray-100 m-8">
            <div className="flex justify-between items-center mb-6 pb-6 border-b">
                <div>
                    <h2 className="text-2xl font-extrabold text-indigo-900">Session Planner</h2>
                    
                    {/* Updated Header with Icon */}
                    <div className="flex items-center gap-2 mt-1 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-100 w-fit">
                        <span className="text-indigo-400">📅</span>
                        <span className="text-indigo-800 text-sm font-bold">
                            Dr. {doctorNameDisplay || doctor?.full_name || '...'}
                        </span> 
                    </div>
                </div>
                <Link to="/reception/schedule" className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50 font-medium">
                    &larr; View Appointment List
                </Link>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                
                {/* 1. CREATION PANEL */}
                <div className="lg:col-span-1">
                    <div className="bg-indigo-50 p-6 rounded-lg border border-indigo-100 sticky top-20">
                        <h3 className="text-lg font-bold text-indigo-800 mb-4 flex items-center gap-2">
                             <span>⚡</span> New Session
                        </h3>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Date</label>
                                <input type="date" required className="w-full p-2 border border-indigo-200 rounded focus:ring-2 focus:ring-indigo-500 bg-white" value={newSession.date} onChange={(e) => setNewSession({...newSession, date: e.target.value})} />
                            </div>
                            
                            <div>
                                <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Shift Type</label>
                                <select className="w-full p-2 border border-indigo-200 rounded focus:ring-2 focus:ring-indigo-500 bg-white" value={newSession.session_name} onChange={(e) => setNewSession({...newSession, session_name: e.target.value})}>
                                    <option>Morning</option>
                                    <option>Afternoon</option>
                                    <option>Evening</option>
                                    <option>Night</option>
                                    <option>Full Day</option>
                                </select>
                            </div>
                            
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Start</label>
                                    <input type="time" className="w-full p-2 border border-indigo-200 rounded text-center font-mono" value={newSession.start_time} onChange={(e) => setNewSession({...newSession, start_time: e.target.value})} />
                                </div>
                                <div className="flex-1">
                                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">End</label>
                                    <input type="time" className="w-full p-2 border border-indigo-200 rounded text-center font-mono" value={newSession.end_time} onChange={(e) => setNewSession({...newSession, end_time: e.target.value})} />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Max Serials</label>
                                <input type="number" className="w-full p-2 border border-indigo-200 rounded" value={newSession.max_patients} onChange={(e) => setNewSession({...newSession, max_patients: e.target.value})} />
                            </div>
                            
                            <button type="submit" className="w-full py-3 bg-indigo-600 text-white rounded-lg font-bold shadow hover:bg-indigo-700 transition transform active:scale-95">
                                Add to Calendar
                            </button>
                        </form>
                    </div>
                </div>

                {/* 2. CALENDAR LIST */}
                <div className="lg:col-span-2">
                    <div className="mb-6 flex justify-between items-end">
                        <h3 className="text-xl font-bold text-gray-800">
                            {viewHistory ? 'Past History (Last 50)' : 'Active & Upcoming Sessions'}
                        </h3>
                        
                        {/* TOGGLE BUTTON */}
                        <button 
                            onClick={() => setViewHistory(!viewHistory)}
                            className={`text-sm px-4 py-2 rounded-full border transition-all ${viewHistory ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                        >
                            {viewHistory ? '← Show Active Sessions' : '📜 View Past History'}
                        </button>
                    </div>
                    
                    {loading ? <div className="p-8 text-center text-gray-400">Loading...</div> : 
                    
                    <div className="space-y-4">
                        {/* Iterate sessions */}
                        {sessions.map(s => {
                             // Status Checks with Timezone safety
                             // Force string parsing instead of Date obj conversion for comparison
                             const sDateStr = String(s.date).substring(0, 10);
                             const todayStr = new Date().toISOString().substring(0, 10);

                             const isExpired = sDateStr < todayStr;
                             const isFull = s.booked_count >= s.max_patients;
                             const percentage = Math.round((s.booked_count / s.max_patients) * 100);
                             
                             // Display Date (Local formatting)
                             const displayDate = new Date(s.date).toLocaleDateString(undefined, {
                                 weekday: 'short', month: 'short', day: 'numeric'
                             });

                            return (
                                <div key={s.schedule_id} className={`flex flex-col md:flex-row items-center border rounded-lg p-4 transition hover:shadow-md ${isExpired ? 'bg-gray-50 opacity-60' : 'bg-white'}`}>
                                    
                                    {/* Left: Date Block */}
                                    <div className="flex flex-col items-center justify-center p-3 rounded-md bg-gray-100 min-w-20 w-24">
                                        <span className="text-xs font-bold uppercase text-gray-500">{displayDate.split(',')[0]}</span>
                                        <span className="text-2xl font-bold text-gray-800">{new Date(s.date).getDate()}</span>
                                        <span className="text-xs font-bold text-gray-500">{new Date(s.date).toLocaleString('default', { month: 'short' })}</span>
                                    </div>

                                    {/* Center: Details */}
                                    <div className="flex-1 md:ml-6 text-center md:text-left my-2 md:my-0">
                                        <div className="flex items-center justify-center md:justify-start gap-3">
                                            <h4 className="font-bold text-lg text-indigo-900">
                                                {s.session_name}
                                            </h4>
                                            {isExpired ? <span className="bg-gray-200 text-gray-600 text-[10px] px-2 py-0.5 rounded uppercase font-bold">Ended</span>
                                            : isFull ? <span className="bg-red-100 text-red-600 text-[10px] px-2 py-0.5 rounded uppercase font-bold">Full</span> 
                                            : <span className="bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded uppercase font-bold">Open</span>}
                                        </div>
                                        
                                        <p className="text-gray-500 text-sm font-mono mt-1 flex items-center gap-1 justify-center md:justify-start">
                                            ⏰ {s.start_time.substring(0,5)} - {s.end_time.substring(0,5)}
                                        </p>
                                        
                                        {/* Progress Line */}
                                        <div className="flex items-center gap-3 mt-3 text-xs w-full max-w-xs mx-auto md:mx-0">
                                            <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                                                <div className={`h-full ${isFull ? 'bg-red-500' : 'bg-green-500'}`} style={{width: `${percentage}%`}}></div>
                                            </div>
                                            <span className="font-bold text-gray-600 whitespace-nowrap">{s.booked_count} / {s.max_patients}</span>
                                        </div>
                                    </div>

                                    {/* Right: Actions */}
                                    <div className="flex md:flex-col gap-2 mt-2 md:mt-0">
                                        <button 
                                            onClick={() => cloneToForm(s)} 
                                            className="text-xs border border-indigo-200 text-indigo-600 px-3 py-1.5 rounded hover:bg-indigo-50 font-bold flex items-center gap-1 justify-center"
                                        >
                                            <span>content_copy</span> Copy Next
                                        </button>
                                        
                                        <button 
                                            onClick={() => handleDelete(s.schedule_id)} 
                                            className="text-xs border border-red-200 text-red-500 px-3 py-1.5 rounded hover:bg-red-50 font-bold"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            );
                        })}

                        {sessions.length === 0 && <p className="text-center py-10 text-gray-400 border-2 border-dashed rounded-lg bg-gray-50">No sessions found. Create your first one!</p>}
                    </div>
                    }
                </div>
            </div>
        </div>
    );
}

export default SessionCalendar;